import { useMemo } from "react";
import { shouldQueryProjectId, type RemoteServer } from "./useProjects";
import { useProjectServersQuery, useServerAttachments, useServerMutations as useDataServerMutations } from "@/data/servers";

// Saved views (mcpAppViews / openaiAppViews) were a Convex-only surface with
// no sphinx endpoint; the queries resolve to an empty list and the mutations
// reject. Server lookups go through the sphinx REST seam (`@/data/servers`).

// Type definitions matching backend
export type ViewProtocol = "mcp-apps" | "openai-apps";

// Re-exported from the shared module so the playground hook, eval
// fanout, and synthetic runner all reference the same definitions.
// See `shared/widget-snapshot.ts` for the source of truth. The
// `import type` brings the names into local scope so type references
// below (`defaultContext: DisplayContext`, `widgetCsp?: WidgetCsp`)
// resolve — `export type { ... }` alone is a pure re-export and does
// not create a local binding.
import type {
  SharedChatWidgetDisplayContext as DisplayContext,
  SharedChatWidgetCsp as WidgetCsp,
} from "@/shared/widget-snapshot";

export type { DisplayContext, WidgetCsp };

export type ServerInfo = {
  name: string;
  iconUrl?: string;
};

// Base view type
export interface ViewBase {
  _id: string;
  projectId: string;
  serverId: string;
  name: string;
  description?: string;
  toolName: string;
  toolState: "output-available" | "output-error";
  toolInput: unknown;
  toolOutputBlob: string;
  toolOutputUrl: string | null;
  toolErrorText?: string;
  toolMetadata?: unknown;
  prefersBorder?: boolean;
  tags?: string[];
  category?: string;
  defaultContext?: DisplayContext;
  createdBy: string;
  updatedBy?: string;
  createdAt: number;
  updatedAt: number;
  /**
   * Whether the cached widget HTML blob (`widgetHtmlUrl`) was
   * captured with the OpenAI Apps SDK `window.openai` shim injected.
   * Surfaced from the backend `mcpAppViews` / `openaiAppViews` row.
   * Absent on pre-feature rows (`undefined` → renderer falls back to
   * the live host's compat flag for non-cached fetches).
   */
  injectedOpenAiCompat?: boolean;
}

// MCP-specific view
export interface McpAppView extends ViewBase {
  protocol: "mcp-apps";
  resourceUri: string;
  toolsMetadata?: unknown;
  widgetCsp?: WidgetCsp;
  widgetPermissions?: unknown;
  widgetPermissive?: boolean;
  /** URL to cached widget HTML for offline rendering */
  widgetHtmlUrl?: string | null;
}

// OpenAI-specific view
export interface OpenaiAppView extends ViewBase {
  protocol: "openai-apps";
  outputTemplate: string;
  serverInfo?: ServerInfo;
  widgetState?: unknown;
  /** URL to cached widget HTML for offline rendering */
  widgetHtmlUrl?: string | null;
}

// Union type for any view
export type AnyView = McpAppView | OpenaiAppView;

const NO_VIEWS: AnyView[] = [];
const EMPTY_BY_SERVER = new Map<string, AnyView[]>();
const EMPTY_BY_CATEGORY = new Map<string, AnyView[]>();
const EMPTY_BY_PROTOCOL = { mcp: [] as McpAppView[], openai: [] as OpenaiAppView[] };

// Query hook for fetching views
export function useViewQueries(_args: {
  isAuthenticated: boolean;
  projectId: string | null;
}) {
  return {
    views: NO_VIEWS,
    sortedViews: NO_VIEWS,
    viewsByServer: EMPTY_BY_SERVER,
    viewsByCategory: EMPTY_BY_CATEGORY,
    viewsByProtocol: EMPTY_BY_PROTOCOL,
    isLoading: false,
    hasViews: false,
  };
}

const viewsUnsupported = async (..._args: unknown[]): Promise<never> => {
  throw new Error("Saved views are not available in sphinx.");
};

const VIEW_MUTATIONS = {
  createMcpView: viewsUnsupported,
  updateMcpView: viewsUnsupported,
  removeMcpView: viewsUnsupported,
  generateMcpUploadUrl: viewsUnsupported,
};

// Mutation hook for view operations
export function useViewMutations() {
  return VIEW_MUTATIONS;
}

// Hook to get servers for a project (for server ID resolution)
export function useProjectServers({
  isAuthenticated,
  projectId,
}: {
  isAuthenticated: boolean;
  projectId: string | null;
}) {
  const enableQuery = isAuthenticated && shouldQueryProjectId(projectId);

  const servers = useProjectServersQuery(
    enableQuery ? { projectId: projectId!.trim() } : "skip"
  ) as RemoteServer[] | undefined;

  const isLoading = enableQuery && servers === undefined;

  // Create a map for quick lookup by name
  const serversByName = useMemo(() => {
    if (!servers) return new Map<string, string>();
    const map = new Map<string, string>();
    for (const server of servers) {
      map.set(server.name, server._id);
    }
    return map;
  }, [servers]);

  // Create a map for reverse lookup by ID
  const serversById = useMemo(() => {
    if (!servers) return new Map<string, string>();
    const map = new Map<string, string>();
    for (const server of servers) {
      map.set(server._id, server.name);
    }
    return map;
  }, [servers]);

  return {
    servers,
    serversByName,
    serversById,
    isLoading,
  };
}

export function useProjectServerAttachments({
  isAuthenticated,
  projectId,
}: {
  isAuthenticated: boolean;
  projectId: string | null;
}) {
  const enableQuery = isAuthenticated && shouldQueryProjectId(projectId);
  const serverAttachments = useServerAttachments(
    enableQuery ? { projectId: projectId!.trim() } : "skip"
  );
  return { serverAttachments: serverAttachments ?? [], isLoading: false };
}

// Server mutation for creating servers
export function useServerMutations() {
  const { createServer, createServerIfMissing } = useDataServerMutations();
  return { createServer, createServerIfMissing };
}
